import { memo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { DropdownButton, TabbedDropdown } from "@similarweb/ui-components";

import { DropdownSmartTabs } from "app/pages/sales-intelligence/common-components/dropdown/DropdownTabs/DropdownTabs";
import type { TechnographicsCellType } from "app/pages/sales-intelligence/common-components/export-table/cells/types";
import { usePreDefinedValidation } from "app/pages/sales-intelligence/common-components/export-table/hooks/usePreDefinedValidation";
import { StyledTechnographicsWrapper } from "app/pages/sales-intelligence/common-components/export-table/styles";
import technographicsService from "app/pages/sales-intelligence/common-components/export-table/technographicsService";
import { createPlacement } from "app/pages/sales-intelligence/common-components/export-table/utils";
import { EMPTY_ARRAY } from "app/pages/sales-intelligence/constants/common";
import DropdownTabPanel from "app/pages/sales-intelligence/sub-modules/advanced-search/components/shared/TabbedDropdown/DropdownTabPanel/DropdownTabPanel";
import useTechnologiesSearch from "app/pages/sales-intelligence/sub-modules/advanced-search/hooks/useTechnologiesSearch";
import useTechnologiesTabs from "app/pages/sales-intelligence/sub-modules/advanced-search/hooks/useTechnologiesTabs";
import useToggleState from "app/pages/sales-intelligence/sub-modules/advanced-search/hooks/useToggleState";

import useTechnologiesListItemsQuery, { EMPTY_TECHNOLOGIES_LIST_ITEMS } from "../queries/useTechnologiesListItemsQuery";
import type { TechnologiesConditionEntry, TechnologiesDDItemType } from "../types/technologies";

const TechnographicsCell = ({ value, setValue, tableLength, index, setting, dataType }: TechnographicsCellType) => {
    const { translate } = useTranslate();

    const { data = EMPTY_TECHNOLOGIES_LIST_ITEMS } = useTechnologiesListItemsQuery();

    const displayName = technographicsService.getDisplayName(value);
    const entry = technographicsService.getEntry(value);

    const { placement } = createPlacement(tableLength, index, "technographics");

    const [isOpen, setIsOpen] = useToggleState(false);

    const { sortedItems, searchTerm, searchProps } = useTechnologiesSearch(data, { shouldProcessItems: isOpen });

    const { tabs } = useTechnologiesTabs({
        isOpen,
        disabledEntries: EMPTY_ARRAY,
        selectedEntries: entry ? [entry] : EMPTY_ARRAY,
        sortedItems,
    });

    const { validate } = usePreDefinedValidation(setting);

    const handleChange = (newEntry: TechnologiesConditionEntry) => {
        const newValue = technographicsService.setValueFromEntry(newEntry);
        setValue(newValue);
        setIsOpen(false);
        validate(dataType, newValue);
    };

    const handleItemClick = (item: TechnologiesDDItemType) => {
        const { name, type } = item;
        handleChange({ name, type });
    };

    const renderButton = () => {
        return (
            <DropdownButton>
                {displayName || translate("si.management.integration.export.cell.example.technographics.placeholder")}
            </DropdownButton>
        );
    };

    return (
        <StyledTechnographicsWrapper
            placement={placement}
            hasValue={!!value}
        >
            <TabbedDropdown
                isOpen={isOpen}
                onToggle={setIsOpen}
                onItemClick={handleItemClick}
                isSingleSelect
                height={180}
                buttonComponent={renderButton}
                tabs={tabs}
                searchProps={searchProps}
                searchTerm={searchTerm}
                panelComponent={DropdownTabPanel}
                tabsView={DropdownSmartTabs}
            />
        </StyledTechnographicsWrapper>
    );
};

export default memo(TechnographicsCell);
